import React, { useMemo, useState } from 'react';
import { Box, Button, Divider, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { themeColors, useGlobalContext } from '@applications-terrains/birdz-react-library';
import { useTranslation } from 'react-i18next';
import ModalWithLoader, {
  RequestStatus
} from '../../../../../Tools/ModalWithLoader/ModalWithLoader';
import {
  useGetBConnectInterventionTypes,
  useGetConfigurations,
  useGetRadioModes
} from '../../../../../../hooks/datarefs';
import { translateType } from '../../../NewIntervention/Steps/utils';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import {
  EMPTY_QUALIFICATION_DRAFT,
  QualificationDraft,
  TARGET_CRITERIA_OPTIONS,
  configurationToSelectValue,
  selectValueToConfiguration,
  buildConfigurationFilters,
  getScheduleError,
  suggestAppointmentEnd
} from './qualification';
import AppointmentSection from './AppointmentSection';
import SectionTitle from './SectionTitle';
import { useQualifyRequests } from './useQualifyRequests';
import { useSetInterventionType } from './useSetInterventionType';
import { useRequestsToQualify } from './useRequestsToQualify';
import QualifyReportMessage from './QualifyReportMessage';

/** Valeur du select quand rien n'est choisi : `null` ne peut pas servir de value MUI. */
const NO_SELECTION = '';

/** Hauteur de la modale de qualification, calée sur un bilan dont la liste des refus défile. */
const QUALIFY_MODAL_MIN_HEIGHT = 460;

export default function QualificationPanel() {
  const { t } = useTranslation();
  const { state, dispatch } = useInterventionCreation();
  const qualifyState = useQualifyRequests();
  const interventionTypeState = useSetInterventionType();
  // Accentuation du thème choisi par l'utilisateur, pour distinguer l'encart du champ
  // qui s'enregistre immédiatement du reste du formulaire.
  const { theme } = useGlobalContext();
  // Sans filtre : même clé de requête que le compteur du stepper, donc même cache.
  const { count: toQualifyCount } = useRequestsToQualify();

  const [draft, setDraft] = useState<QualificationDraft>(EMPTY_QUALIFICATION_DRAFT);

  const { data: interventionTypes } = useGetBConnectInterventionTypes();
  const { data: radioModes } = useGetRadioModes();
  const { data: configurations } = useGetConfigurations(
    state.clientId === null ? '' : String(state.clientId),
    buildConfigurationFilters(draft)
  );

  const interventionTypeOptions = useMemo(
    () =>
      (interventionTypes ?? []).map((type) => ({
        value: type.Id,
        label: translateType(type.Name, t)
      })),
    [interventionTypes, t]
  );

  const hasSelection = state.selectedRequestIds.length > 0;
  const scheduleError = getScheduleError(draft);

  const update = <K extends keyof QualificationDraft>(field: K, value: QualificationDraft[K]) =>
    setDraft((previous) => ({ ...previous, [field]: value }));

  const prefillAppointmentEnd = () =>
    setDraft((previous) => {
      const suggestedEnd = suggestAppointmentEnd(previous);
      return suggestedEnd ? { ...previous, ...suggestedEnd } : previous;
    });

  /**
   * Seul champ à ne pas attendre « Qualifier » : la valeur est enregistrée dès sa
   * sélection. On garde le choix affiché même en cas d'échec, le message sous le
   * champ signalant que rien n'a été sauvegardé.
   */
  const selectInterventionType = (value: string) => {
    const code = value === NO_SELECTION ? null : Number(value);
    update('interventionTypeCode', code);

    if (code !== null) {
      interventionTypeState.setInterventionType(code);
    }
  };

  /**
   * Changer le protocole ou le critère change la liste des configurations proposées :
   * on repart de « Par défaut » plutôt que de conserver un id devenu invalide, comme
   * le faisait la v1.
   */
  const updateConfigurationFilter = (
    field: 'radioMode' | 'targetCriteria',
    value: QualificationDraft[typeof field]
  ) =>
    setDraft((previous) => ({
      ...previous,
      [field]: value,
      targetConfiguration: null
    }));

  const { report } = qualifyState;
  // Une demande incomplète n'est pas un échec, mais elle demande une action : le même
  // avertissement couvre les deux cas.
  const needsAttention = report !== null && (report.incompleteCount > 0 || report.failedCount > 0);

  const requestStatus: RequestStatus = qualifyState.isQualifying
    ? 'pending'
    : qualifyState.hasFailed
      ? 'error'
      : needsAttention
        ? 'warning'
        : report !== null
          ? 'success'
          : null;

  /**
   * Qualifier ne montre rien quand tout passe : la modale est réservée aux bilans à
   * lire. Le bilan part donc au contexte, qu'un bandeau affiche au-dessus de la table.
   */
  const qualifyAndReport = () =>
    qualifyState.qualify(draft, {
      onSuccess: (result) => {
        const stillIncomplete = Object.keys(result.missing_fields).length > 0;

        // Un bilan partiel ouvre déjà la modale : deux retours pour un seul geste.
        if (stillIncomplete || result.failed_ids.length > 0 || result.qualified_ids.length === 0) {
          return;
        }

        /*
         * `toQualifyCount` date d'avant l'invalidation : la soustraction dit ce qu'il
         * restera. Plus rien à qualifier, donc plus rien à faire sur cette étape.
         *
         * Le total doit être connu pour trancher : il vaut 0 tant que la requête n'a
         * pas répondu, et une soustraction naïve conclurait alors à tort que l'étape
         * est vide. On vient d'en qualifier, donc un total nul ne peut être que celui
         * d'un compteur pas encore chargé.
         */
        const isCountKnown = toQualifyCount > 0;
        const willRedirect = isCountKnown && toQualifyCount - result.qualified_ids.length <= 0;

        // Qualifier envoie les demandes à l'étape suivante : même aboutissement que le
        // bouton d'envoi, donc même bandeau. Le retour anticipé ci-dessus garantit qu'on
        // n'arrive ici qu'en succès complet, d'où l'absence d'échecs à rapporter.
        dispatch({
          type: 'reportSendToAssignment',
          report: {
            succeededCount: result.qualified_ids.length,
            failedCount: 0,
            failures: []
          }
        });

        if (willRedirect) {
          dispatch({ type: 'goToStep', step: 'assignment' });
        }
      }
    });

  return (
    <>
      <ModalWithLoader
        // Le succès est volontairement exclu : `ModalWithLoader` n'arme sa fermeture
        // automatique que quand `openModal` repasse à faux. La modale reste montée le
        // temps d'afficher le bilan — `status` n'est pas nul — puis se ferme seule.
        // Un avertissement, lui, doit être lu : il maintient la modale ouverte.
        openModal={requestStatus !== null && requestStatus !== 'success'}
        status={requestStatus}
        // `ModalWithLoader` préfixe le seul message de succès par `action`, et sans
        // séparateur : on obtiendrait « Qualification2 demandes d'intervention
        // qualifiées envoyées en affectation ». On neutralise le préfixe pour ce statut,
        // en le gardant pour « en cours ». Le
        // composant partagé ne peut pas changer : d'autres écrans construisent au
        // contraire leur phrase sur cette concaténation.
        action={requestStatus === 'success' ? '' : t('interventionCreation.qualification.title')}
        customErrorMessage={t('interventionCreation.qualification.error')}
        customWarningMessage={
          report !== null && needsAttention ? <QualifyReportMessage report={report} /> : null
        }
        // Le bilan, avec son titre et ses refus, dépasse la hauteur par défaut : sans
        // marge, le bouton de fermeture sortirait de la modale.
        contentMinHeight={QUALIFY_MODAL_MIN_HEIGHT}
        customSuccessMessage={
          report !== null
            ? t('interventionCreation.qualification.success', { count: report.qualifiedCount })
            : undefined
        }
        onClose={qualifyState.dismiss}
        onSuccess={qualifyState.dismiss}
      />

      <Paper sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
        <Stack spacing={1.5}>
          <Typography variant="h6">{t('interventionCreation.qualification.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('interventionCreation.qualification.subtitle')}
          </Typography>

          {!hasSelection ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                minHeight: 120,
                justifyContent: 'center',
                p: 2
              }}
            >
              <CheckBoxOutlinedIcon color="disabled" />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {t('interventionCreation.qualification.emptyState')}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {/*
                Ce champ s'applique immédiatement à la sélection, contrairement aux
                suivants : l'encart et la mention sous le select le signalent.
              */}
              <Box
                sx={{
                  bgcolor: 'action.hover',
                  borderLeft: 3,
                  borderColor: themeColors[theme],
                  borderRadius: 1,
                  p: 1.5
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="body2" id="qualification-type-label">
                    {`${t('interventionCreation.qualification.interventionType')} *`}
                  </Typography>
                  <TextField
                    select
                    id="qualification-type"
                    fullWidth
                    size="small"
                    // Le fond se pose sur le champ et non sur le TextField : l'arrondi est
                    // porté par `.MuiOutlinedInput-root`, un fond posé sur le conteneur
                    // déborderait des angles.
                    InputProps={{ sx: { bgcolor: 'background.paper' } }}
                    SelectProps={{ labelId: 'qualification-type-label' }}
                    value={draft.interventionTypeCode ?? NO_SELECTION}
                    disabled={interventionTypeState.isSaving}
                    onChange={(event) => selectInterventionType(event.target.value)}
                  >
                    {interventionTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <BoltOutlinedIcon sx={{ fontSize: 16 }} color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {t('interventionCreation.qualification.interventionTypeImmediate', {
                        total: state.selectedRequestIds.length
                      })}
                    </Typography>
                  </Stack>
                  {interventionTypeState.hasFailed && (
                    <Typography variant="caption" color="error">
                      {t('interventionCreation.qualification.interventionTypeError')}
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Divider />

              <SectionTitle>
                {t('interventionCreation.qualification.additionalInformation')}
              </SectionTitle>

              <Stack spacing={1}>
                <Typography variant="body2" component="label" htmlFor="qualification-cause">
                  {t('interventionCreation.qualification.cause')}
                </Typography>
                <TextField
                  id="qualification-cause"
                  fullWidth
                  size="small"
                  value={draft.cause}
                  onChange={(event) => update('cause', event.target.value)}
                />
              </Stack>

              <Stack spacing={1}>
                <Typography variant="body2" component="label" htmlFor="qualification-instructions">
                  {t('interventionCreation.qualification.instructions')}
                </Typography>
                <TextField
                  id="qualification-instructions"
                  fullWidth
                  multiline
                  rows={3}
                  value={draft.instructions}
                  onChange={(event) => update('instructions', event.target.value)}
                  placeholder={t('interventionCreation.qualification.instructionsPlaceholder')}
                />
              </Stack>

              <Divider />

              <AppointmentSection
                draft={draft}
                error={scheduleError}
                onChange={update}
                onEndFocus={prefillAppointmentEnd}
              />

              <Divider />

              <SectionTitle>
                {t('interventionCreation.qualification.moduleConfiguration')}
              </SectionTitle>

              <Stack spacing={1}>
                <Typography variant="body2" id="qualification-protocol-label">
                  {`${t('interventionCreation.qualification.targetProtocol')} *`}
                </Typography>
                <TextField
                  select
                  id="qualification-protocol"
                  fullWidth
                  size="small"
                  SelectProps={{ labelId: 'qualification-protocol-label', displayEmpty: true }}
                  value={draft.radioMode ?? NO_SELECTION}
                  onChange={(event) =>
                    updateConfigurationFilter(
                      'radioMode',
                      event.target.value === NO_SELECTION ? null : Number(event.target.value)
                    )
                  }
                >
                  {(radioModes ?? []).map((option) => (
                    <MenuItem key={String(option.value)} value={option.value ?? NO_SELECTION}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="body2" id="qualification-criteria-label">
                  {`${t('interventionCreation.qualification.specificCriteria')} *`}
                </Typography>
                <TextField
                  select
                  id="qualification-criteria"
                  fullWidth
                  size="small"
                  SelectProps={{ labelId: 'qualification-criteria-label', displayEmpty: true }}
                  value={
                    draft.targetCriteria === null ? NO_SELECTION : String(draft.targetCriteria)
                  }
                  onChange={(event) =>
                    updateConfigurationFilter(
                      'targetCriteria',
                      event.target.value === NO_SELECTION ? null : event.target.value === 'true'
                    )
                  }
                >
                  {TARGET_CRITERIA_OPTIONS.map((option) => (
                    <MenuItem
                      key={String(option.value)}
                      value={option.value === null ? NO_SELECTION : String(option.value)}
                    >
                      {t(option.labelKey)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="body2" id="qualification-configuration-label">
                  {`${t('interventionCreation.qualification.configuration')} *`}
                </Typography>
                <TextField
                  select
                  id="qualification-configuration"
                  fullWidth
                  size="small"
                  SelectProps={{ labelId: 'qualification-configuration-label' }}
                  value={configurationToSelectValue(draft.targetConfiguration)}
                  onChange={(event) =>
                    update('targetConfiguration', selectValueToConfiguration(event.target.value))
                  }
                >
                  {/* « Par défaut » ouvre la liste renvoyée par l'API et porte la valeur
                      initiale du champ : pas d'invite à choisir, le formulaire part d'un
                      choix que l'API accepte. */}
                  {(configurations ?? []).map((option) => (
                    <MenuItem
                      key={String(option.value)}
                      value={configurationToSelectValue(option.value)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1 }}>
                <Button variant="outlined" onClick={() => setDraft(EMPTY_QUALIFICATION_DRAFT)}>
                  {t('interventionCreation.qualification.reset')}
                </Button>
                <Button
                  variant="contained"
                  disabled={qualifyState.isQualifying || scheduleError !== null}
                  onClick={qualifyAndReport}
                >
                  {t('interventionCreation.qualification.qualify', {
                    total: state.selectedRequestIds.length
                  })}
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      </Paper>
    </>
  );
}
